// src/landing/PricingGrid.jsx
// ─── Grille tarifaire unique (§37-48, §100) ─────────────────────────────────
// Les MONTANTS viennent du serveur (/api/public/pricing), jamais du frontend :
// prix de base, promotion de lancement et remise Business Developer y sont
// calculés à partir de plan_promotions et du programme partenaires (§48).
// La configuration locale ne fournit que les libellés et les capacités.
//
// §42, §46 — La remise apporteur n'est affichée que si un code valide est
// présent, et selon la règle réellement renvoyée : si le backend dit
// « non cumulable », on l'écrit au lieu de calculer un total inventé.

import { useState, useEffect } from "react";
import { getPublicPricing, pricePeriodLabel } from "../config/pricing";

const eur = (c) => `${Math.round((c || 0) / 100).toLocaleString("fr-FR")} €`;

export default function PricingGrid({ onSelect, onQuote, detailed = false, apiUrl = "", refCode = null }) {
  const [period, setPeriod] = useState("monthly");
  const [server, setServer] = useState(null);
  const [bd, setBd] = useState(null);
  const plans = getPublicPricing();

  useEffect(() => {
    const q = refCode ? `?ref=${encodeURIComponent(refCode)}` : "";
    fetch(`${apiUrl}/api/public/pricing${q}`).then(r => r.json())
      .then(j => { if (j?.success) { setServer(j.pricing || {}); setBd(j.businessDeveloper || null); } })
      .catch(() => {});
  }, [apiUrl, refCode]);

  const priceBlock = (p) => {
    if (p.quote) return <><div className="lv2-plan-price">Sur devis</div><div className="lv2-plan-per">{p.audience}</div></>;
    if (p.id === "trial") return <><div className="lv2-plan-price">0 €</div><div className="lv2-plan-per">7 jours gratuits</div></>;

    const data = server?.[p.id]?.periods?.[period];
    // Tant que le serveur n'a pas répondu, on n'affiche AUCUN montant plutôt
    // qu'un prix de repli qui pourrait être faux (§57 de la passe précédente).
    if (!data) return <><div className="lv2-plan-price lv2-plan-price-load">—</div><div className="lv2-plan-per">Chargement du tarif…</div></>;

    const launch = data.launch;
    return (
      <>
        {launch.active && (
          <div className="lv2-plan-launch">
            <span className="lv2-plan-old">{eur(launch.originalCents)}</span>
            <span className="lv2-badge gold">{launch.label}</span>
          </div>
        )}
        <div className="lv2-plan-price">{eur(data.priceCents)}</div>
        <div className="lv2-plan-per">{pricePeriodLabel(p, period)}</div>
        {data.businessDeveloper && (
          data.businessDeveloper.not_stackable
            ? <div className="lv2-plan-bd">Code {data.businessDeveloper.code} : non cumulable avec l'offre de lancement.</div>
            : <div className="lv2-plan-bd">Code Business Developer {data.businessDeveloper.code} : <strong>{eur(data.businessDeveloper.priceCents)}</strong> <span>(−{data.businessDeveloper.percent} %)</span></div>
        )}
      </>
    );
  };

  return (
    <>
      {detailed && (
        <div className="lv2-period" role="tablist" aria-label="Périodicité">
          {[["monthly", "Mensuel"], ["yearly", "Annuel"]].map(([k, l]) => (
            <button key={k} role="tab" aria-selected={period === k} className={`lv2-period-btn${period === k ? " on" : ""}`} onClick={() => setPeriod(k)}>{l}</button>
          ))}
        </div>
      )}
      {bd && <div className="lv2-bd-banner">Code Business Developer <strong>{bd.code}</strong> reconnu — remise de {bd.percent} % appliquée à la souscription.</div>}
      <div className="lv2-pricing">
        {plans.map(p => (
          <div key={p.id} className={`lv2-card lv2-plan${p.highlight ? " best" : ""}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: ".5rem" }}>
              <div className="lv2-plan-name">{p.label}</div>
              {p.highlight && <span className="lv2-badge gold">Le plus choisi</span>}
            </div>
            <div className="lv2-plan-tag">{p.tagline}</div>
            {priceBlock(p)}
            <ul className="lv2-plan-list">{(detailed ? p.features.slice(0, 10) : p.features.slice(0, 5)).map(f => <li key={f}>{f}</li>)}</ul>
            {detailed && p.note && <div className="lv2-plan-note">{p.note}</div>}
            {/* §48 — On transmet plan.id et la période, jamais un montant. */}
            <button className={`lv2-btn ${p.highlight ? "primary" : "secondary"} md`}
              onClick={() => (p.quote ? onQuote() : onSelect(p.id, period, bd?.code || null))}>{p.cta}</button>
          </div>
        ))}
      </div>
    </>
  );
}
